'use client';

import styles from './MeasurementPlan.module.scss';
import { Loader2, Sparkles } from 'lucide-react';
import pageStyles from '@/src/app/[lang]/north-star-dashboard/page.module.scss';

interface PromptFormProps {
  sector: string;
  sectors: { id: string; label: { fr: string; en: string } }[];
  prompt: string;
  isAnalyzing: boolean;
  isEn: boolean;
  onSectorChange: (value: string) => void;
  onPromptChange: (value: string) => void;
  onSubmit: () => void;
}

export function PromptForm({
  sector,
  sectors,
  prompt,
  isAnalyzing,
  isEn,
  onSectorChange,
  onPromptChange,
  onSubmit,
}: PromptFormProps) {
  const l = (obj: { fr: string; en: string }) => (isEn ? obj.en : obj.fr);

  const labels = {
    sectorLabel: isEn ? 'Sector' : 'Secteur',
    inputLabel: isEn ? 'Your situation or idea' : 'Votre situation ou idée',
    inputHint: isEn
      ? 'The more context you give us (process, pain points, existing tools, success criteria), the sharper the plan.'
      : 'Plus vous nous donnez de contexte (process, frein, outils existants, critères de succès), plus le plan sera affûté.',
    inputPlaceholder: isEn
      ? 'Describe the friction you want to turn into a measurable MVP...'
      : 'Décrivez la friction que vous voulez transformer en MVP mesurable...',
    cta: isEn ? 'Analyze my MVP' : 'Analyser mon MVP',
    analyzing: isEn ? 'Analysis in progress...' : 'Analyse en cours...',
  };

  function classNames(...classes: (string | false | undefined)[]) {
    return classes.filter(Boolean).join(' ');
  }

  return (
    <div className={styles.promptForm}>
      <div className={classNames(pageStyles.field, pageStyles.fieldGrow)}>
        <label htmlFor="prompt" className={pageStyles.fieldLabel}>
          {labels.inputLabel}
        </label>
        <textarea
          id="prompt"
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder={labels.inputPlaceholder}
          rows={5}
          className={classNames(pageStyles.textarea, pageStyles.textareaLarge)}
        />
        <p className={pageStyles.fieldHint}>{labels.inputHint}</p>
      </div>

      <div className={pageStyles.inputRow}>
        <div className={pageStyles.field}>
          <label htmlFor="sector" className={pageStyles.fieldLabel}>
            {labels.sectorLabel}
          </label>
          <select
            id="sector"
            value={sector}
            onChange={(e) => onSectorChange(e.target.value)}
            className={pageStyles.select}
          >
            {sectors.map((s) => (
              <option key={s.id} value={s.id}>
                {l(s.label)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <button
        type="button"
        className={pageStyles.cta}
        disabled={!prompt.trim() || isAnalyzing}
        onClick={onSubmit}
      >
        {isAnalyzing ? (
          <>
            <Loader2 size={18} className={pageStyles.graphLoaderSpin} />
            {labels.analyzing}
          </>
        ) : (
          <>
            <Sparkles size={18} />
            {labels.cta}
          </>
        )}
      </button>
    </div>
  );
}
